'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { User, Users } from 'lucide-react'
import type { ContactOrUser } from '@/components/share-room/types'

/** Same shape as share modal directory rows (`ContactOrUser`). */
export type CommunityInviteTableRow = ContactOrUser

export type CommunityInviteResultsTableProps = {
  items: CommunityInviteTableRow[]
  selectedIds: Set<string>
  onToggleRow: (item: CommunityInviteTableRow, checked: boolean) => void
  onToggleSelectAllVisible: () => void
  accountName?: string
  /** Same-tenant / workspace name shown for directory users instead of the word “Internal”. */
  workspaceName?: string
  className?: string
}

export function CommunityInviteResultsTable({
  items,
  selectedIds,
  onToggleRow,
  onToggleSelectAllVisible,
  accountName,
  workspaceName,
  className,
}: CommunityInviteResultsTableProps) {
  const visibleCount = items.length
  const selectedInView = items.filter((i) => selectedIds.has(i.id)).length
  const allVisibleSelected = visibleCount > 0 && selectedInView === visibleCount
  const someVisibleSelected = selectedInView > 0 && selectedInView < visibleCount

  return (
    <div className={cn('rounded-md border border-slate-200 bg-white', className)}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10 pl-3">
              <Checkbox
                aria-label="Select all in this list"
                checked={
                  allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false
                }
                onCheckedChange={() => onToggleSelectAllVisible()}
                disabled={visibleCount === 0}
              />
            </TableHead>
            <TableHead className="min-w-[120px]">Name</TableHead>
            <TableHead className="min-w-[140px] hidden sm:table-cell">Email</TableHead>
            <TableHead className="min-w-[100px] hidden md:table-cell">Title</TableHead>
            <TableHead className="w-[120px] text-right pr-3">Type</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const checked = selectedIds.has(item.id)
            return (
              <TableRow key={item.id}>
                <TableCell className="pl-3">
                  <Checkbox
                    aria-label={`Select ${item.firstName} ${item.lastName}`}
                    checked={checked}
                    onCheckedChange={(c) => onToggleRow(item, c === true)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage
                        src={
                          item.image ||
                          `https://api.dicebear.com/6.x/initials/svg?seed=${item.firstName} ${item.lastName}`
                        }
                        alt=""
                      />
                      <AvatarFallback className="text-[10px]">
                        {item.firstName[0]}
                        {item.lastName[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-slate-900">
                      {item.firstName} {item.lastName}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground text-xs">
                  {item.email}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground text-xs">
                  {item.title || '—'}
                </TableCell>
                <TableCell className="text-right pr-3">
                  <Badge
                    variant={item.type === 'user' ? 'outline' : 'secondary'}
                    className={
                      item.type === 'user'
                        ? 'bg-slate-100 text-slate-800 shrink-0 whitespace-nowrap'
                        : 'bg-emerald-600 text-white hover:bg-emerald-600 shrink-0 whitespace-nowrap'
                    }
                  >
                    {item.type === 'user' ? (
                      <>
                        <Users className="h-3 w-3 mr-1 inline" aria-hidden />
                        {workspaceName?.trim() || 'Workspace'}
                      </>
                    ) : (
                      <>
                        <User className="h-3 w-3 mr-1 inline" aria-hidden />
                        {item.accountName || accountName || 'Contact'}
                      </>
                    )}
                  </Badge>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
